import type { TemplateAttribute } from '@/services/hooks/template_attributes'
import { DEFAULT_STROKE_COLOR } from './constants'
import { DEFAULT_ANNOTATIONS_MENUS } from './menus'
import type {
  KonvaAnnotationKind,
  KonvaAnnotationTemplate,
} from './annotationSchema'

const LEGACY_TYPE_TO_KIND: Record<string, KonvaAnnotationKind> = {
  circle: 'circle',
  diamond: 'diamond',
  ellipse: 'ellipse',
  roundRect: 'roundRect',
  square: 'rect',
  triangle: 'triangle',
}

const COLOR_EDITABLE_ANNOTATION_IDS = new Set(
  DEFAULT_ANNOTATIONS_MENUS.filter((menu) => menu.colorOptionsEnabled).flatMap(
    (menu) => menu.menuItems.map((item) => item.id),
  ),
)

export const isLegacyLineBasedAnnotationType = (type?: string | null) =>
  typeof type === 'string' &&
  (type === 'line' || type.includes('arrow') || type.includes('bubble'))

export const getLineBasedAnnotationKind = (
  type: string,
): Extract<KonvaAnnotationKind, 'line' | 'arrow' | 'bubble'> =>
  type === 'line' ? 'line' : type.includes('bubble') ? 'bubble' : 'arrow'

export const getAnnotationKindForLegacyType = (
  type: string,
): KonvaAnnotationKind => {
  if (isLegacyLineBasedAnnotationType(type)) {
    return getLineBasedAnnotationKind(type)
  }

  return LEGACY_TYPE_TO_KIND[type] ?? 'icon'
}

export const isColorEditableKonvaAnnotation = (
  annotation: KonvaAnnotationTemplate,
) => {
  if (
    annotation.legacyType &&
    COLOR_EDITABLE_ANNOTATION_IDS.has(annotation.legacyType)
  ) {
    return true
  }

  switch (annotation.kind) {
    case 'rect':
    case 'roundRect':
    case 'ellipse':
    case 'circle':
    case 'diamond':
    case 'triangle':
    case 'line':
    case 'arrow':
    case 'bubble':
      return true
    default:
      return false
  }
}

export const getSelectedAnnotationColor = ({
  annotation,
  templateValues,
}: {
  annotation: KonvaAnnotationTemplate
  templateValues: TemplateAttribute<any>['template_values']
}) => {
  if (
    !('schema' in (templateValues ?? {})) &&
    typeof templateValues?.strokeColor === 'string'
  ) {
    return templateValues.strokeColor
  }

  return annotation.style.stroke ?? DEFAULT_STROKE_COLOR
}
